<template>
    <div ref="selectorRoot" class="language-selector relative">
        <!-- Globe Icon -->
        <div class="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-4 h-4 text-gray-500"
            >
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
            </svg>
        </div>
        <!-- Dropdown Arrow Icon -->
        <div class="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                class="w-4 h-4 text-gray-500"
            >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
        </div>
        <select
            :value="selectedLanguage"
            @change="handleLanguageChange"
            :class="[
                'language-select appearance-none min-w-[140px] text-sm text-gray-500 bg-white border border-gray-300 transition-shadow hover:bg-gray-50 focus:outline-none focus:ring-0 focus:border-gray-400 pl-9 pr-8 py-2',
                cornersClass,
                customClasses
            ]"
        >
            <option v-for="lang in availableLanguages" :key="lang.value" :value="lang.value">
                {{ lang.label }}
            </option>
        </select>
    </div>
</template>

<script>
import {computed, ref, onMounted, watch} from 'vue';
import {usePortalContentAndDesignStore} from '../../../stores/partners/portalContentAndDesignStore';

export default {
    name: 'LanguageSelector',
    props: {
        customClasses: {
            type: String,
            default: ''
        }
    },
    setup() {
        const portalContentAndDesignStore = usePortalContentAndDesignStore();

        const availableLanguages = computed(() => {
            return portalContentAndDesignStore.availableTranslations || [];
        });

        const selectedLanguage = computed(() => {
            // If no language selected, show default language
            if (!portalContentAndDesignStore.selectedLanguage && portalContentAndDesignStore.defaultLanguage) {
                return portalContentAndDesignStore.defaultLanguage;
            }
            return portalContentAndDesignStore.selectedLanguage || ''; // Empty string means default language
        });

        const cornersClass = computed(() => {
            if (portalContentAndDesignStore.isAuthView) {
                return portalContentAndDesignStore.getPortalLayoutCornersClass || 'rounded';
            }
            return portalContentAndDesignStore.getPublicPageButtonCorners || 'rounded';
        });

        const selectorRoot = ref(null);

        const isContentSidebar = computed(() => {
            if (portalContentAndDesignStore.isAuthView) return false;
            const settings = portalContentAndDesignStore.portalLayoutSettings;
            const type = (portalContentAndDesignStore.signupPageSettings?.signup && settings.portal_public_pages_different_appearance)
                ? (settings.portal_signup_sidebar_type || 'image')
                : (settings.portal_login_sidebar_type || 'image');
            return type === 'content';
        });

        const updateWrapperPosition = () => {
            const wrapper = selectorRoot.value?.parentElement;
            if (!wrapper || !wrapper.classList.contains('language-selector-wrapper')) return;
            wrapper.classList.remove('top-6', 'right-6', 'top-8', 'right-8');
            if (isContentSidebar.value) {
                wrapper.classList.add('top-8', 'right-8');
            } else {
                wrapper.classList.add('top-6', 'right-6');
            }
        };

        onMounted(updateWrapperPosition);
        watch(isContentSidebar, updateWrapperPosition);

        const getStorageKey = () => {
            const programId = portalContentAndDesignStore.program?.id;
            return programId ? `portal_language_${programId}` : 'portal_language';
        };

        const handleLanguageChange = async (event) => {
            const newLanguage = event.target.value;
            
            // If user selected default language, clear selection
            const languageToSave = (newLanguage === portalContentAndDesignStore.defaultLanguage || newLanguage === '') ? null : newLanguage;
            
            // Update store
            portalContentAndDesignStore.setSelectedLanguage(languageToSave);
            
            // Save to localStorage for persistence across sessions
            const storageKey = getStorageKey();
            if (languageToSave) {
                localStorage.setItem(storageKey, languageToSave);
            } else {
                localStorage.removeItem(storageKey);
            }
            
            // Save to session via API (null means default language)
            try {
                const response = await fetch(portalContentAndDesignStore.getLanguageStoreRoute, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                    },
                    body: JSON.stringify({ language: languageToSave })
                });
                
                if (response.ok) {
                    // Reload page to apply translations
                    window.location.reload();
                }
            } catch (error) {
                console.error('Failed to save language preference:', error);
            }
        };

        return {
            selectorRoot,
            availableLanguages,
            selectedLanguage,
            handleLanguageChange,
            cornersClass,
            portalContentAndDesignStore
        };
    }
}
</script>

<style scoped>
.language-selector {
    display: inline-block;
}

.language-select {
    cursor: pointer;
}
</style>

